import { useEffect, useState } from "react";

/**
 * On-page debug panel showing the last 10 analytics events fired.
 *
 * Listens to the `sailkha:analytics` CustomEvent dispatched by
 * `src/lib/analytics.ts` whenever an event is pushed to dataLayer / gtag.
 * Use this to verify GA4 / GTM mappings while testing without opening
 * the GTM preview or GA DebugView.
 *
 * Activation (any of):
 *   - URL contains `?debug=analytics`
 *   - localStorage key `sailkha_debug_analytics` === "1"
 *
 * Toggle persistence at runtime via the panel's pin button.
 */

type AnalyticsEvent = {
  event: string;
  event_id?: string;
  event_at?: string;
  [k: string]: unknown;
};

const STORAGE_KEY = "sailkha_debug_analytics";
const MAX_EVENTS = 10;

function isEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.localStorage.getItem(STORAGE_KEY) === "1") return true;
    const params = new URLSearchParams(window.location.search);
    if (params.get("debug") === "analytics") {
      window.localStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
  } catch { /* ignore */ }
  return false;
}

export function AnalyticsDebugPanel() {
  const [enabled, setEnabled] = useState<boolean>(() => isEnabled());
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<AnalyticsEvent>).detail;
      if (!detail) return;
      setEvents((prev) => [detail, ...prev].slice(0, MAX_EVENTS));
    };
    window.addEventListener("sailkha:analytics", handler);
    return () => window.removeEventListener("sailkha:analytics", handler);
  }, [enabled]);

  if (!enabled) return null;

  const disable = () => {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setEnabled(false);
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 12,
        left: 12,
        zIndex: 99999,
        width: collapsed ? 180 : 380,
        maxHeight: collapsed ? 36 : "60vh",
        background: "rgba(10,10,10,0.92)",
        color: "#fff",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 11,
        border: "1px solid #333",
        boxShadow: "0 6px 24px rgba(0,0,0,0.4)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      role="complementary"
      aria-label="Analytics debug panel"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 8px",
          background: "#1a1a1a",
          borderBottom: collapsed ? "none" : "1px solid #333",
        }}
      >
        <strong style={{ fontSize: 11, letterSpacing: 0.5 }}>
          📊 Analytics ({events.length})
        </strong>
        <div style={{ display: "flex", gap: 4 }}>
          <button
            onClick={() => setEvents([])}
            style={btn}
            title="Clear"
          >
            clear
          </button>
          <button
            onClick={() => setCollapsed((c) => !c)}
            style={btn}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? "▲" : "▼"}
          </button>
          <button onClick={disable} style={btn} title="Disable panel">
            ✕
          </button>
        </div>
      </div>
      {!collapsed && (
        <div style={{ overflow: "auto", padding: 6 }}>
          {events.length === 0 && (
            <div style={{ opacity: 0.6, padding: 6 }}>Waiting for events…</div>
          )}
          {events.map((ev, i) => {
            const { event, event_at, event_id, ...rest } = ev;
            return (
              <div
                key={`${event_id ?? i}-${i}`}
                style={{
                  padding: 6,
                  marginBottom: 4,
                  background: "#181818",
                  borderLeft: "2px solid #4ade80",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ color: "#4ade80", fontWeight: 600 }}>{event}</span>
                  <span style={{ opacity: 0.5 }}>
                    {event_at ? new Date(event_at).toLocaleTimeString() : ""}
                  </span>
                </div>
                <pre
                  style={{
                    margin: "4px 0 0",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: 10,
                    opacity: 0.85,
                  }}
                >
{JSON.stringify(rest, null, 2)}
                </pre>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const btn: React.CSSProperties = {
  background: "transparent",
  color: "#fff",
  border: "1px solid #444",
  fontSize: 10,
  padding: "2px 6px",
  cursor: "pointer",
  fontFamily: "inherit",
};
